import { ButtonDock } from '@focuslee/shared-mini-app';
import { Stack } from '@mui/material';
import { Placeholder } from '@telegram-apps/telegram-ui';
import { useCallback, useMemo, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Page } from '../components/Page';
import { FocusLeePlaceholderImage } from '../components/common/FocusLeePlaceholderImage';
import { QuestSummary } from '../components/quest/QuestSummary';
import { getCategorySummaries } from '../helpers/quest';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useDailyProgress } from '../hooks/useDailyProgress';
import { TodayQuestProgressPageSkeleton } from './skeleton/TodayQuestProgressPageSkeleton';

export const TodayQuestProgressPage: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const { categories, loading: categoriesLoading } = useCachedCategoriesWithFallback();
  const categoryMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);
  const { data: dailyProgressData, loading: dailyProgressLoading } = useDailyProgress({});
  
  const categorySummaries = useMemo(() => {
    if (!dailyProgressData || categoriesLoading || categories.length === 0) return [];
    return getCategorySummaries(dailyProgressData.completedQuests, categoryMap);
  }, [dailyProgressData, categoryMap, categoriesLoading, categories.length]);

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (categoriesLoading || dailyProgressLoading) {
    return <TodayQuestProgressPageSkeleton />;
  }

  const totalXP = dailyProgressData?.totalXP ?? 0;

  return (
    <Page footer={<ButtonDock main={{ text: t('actions.back'), onClick: navigateBack, navigation: 'back' }} />}>
      <Stack height="100%" justifyContent="center">
        {totalXP === 0 ? (
          <Stack direction="column" justifyContent="center" alignItems="center" height="100%">
            <Placeholder
              header={t('quest.noXp.title')}
              description={t('quest.noXp.description')}
              style={{ padding: 0 }}
            >
              <FocusLeePlaceholderImage kind="xp" />
            </Placeholder>
          </Stack>
        ) : (
          <QuestSummary 
            totalXP={totalXP}
            categorySummaries={categorySummaries}
          />
        )}
      </Stack>
    </Page>
  );
};
